export enum TaskStatus {
    NotStarted = "not started",
    Started = "started",
    Completed = "completed",
    Canceled = "canceled",
}

export enum TaskPriority {
    Low = "low",
    Normal = "normal",
    High = "high",
    Urgent = "urgent",
}

export enum EntityParentType {
    Provider = "provider",
    Client = "client",
    Company = "company",
    User = "user",
    Request = "request",
    MissionRequest = "mission_request",
    Mission = "mission",
    Lead = "lead",
    Invoice = "invoice",
    Bill = "bill",
    Payment = "payment",
    Incident = "incident",
    Leg = "leg",
    Itinerary = "itinerary",
}

export interface TaskUserRef {
    userId: number;
    firstName: string;
    lastName: string | null;
    fullName: string;
}

export interface TaskAssignedUserRef {
    userId: number;
    firstName: string;
    lastName: string | null;
    fullName: string;
    photo: string | null;
}

export interface Task {
    taskId: number;
    referenceNumber: string | null;
    name: string | null;
    description: string | null;
    status: TaskStatus | null;
    priority: TaskPriority | null;
    parentType: EntityParentType | null;
    parentId: number | null;
    parentReferenceNumber: string | null;
    scopeLabel?: string | null;
    scopeLegId?: number | null;
    parentTaskId: number | null;
    parentTaskReferenceNumber: string | null;
    sortOrder: number | null;
    startAt: string | null;
    endAt: string | null;
    actualStartAt: string | null;
    actualEndAt: string | null;
    assignedUser: TaskAssignedUserRef | null;
    createdBy: TaskUserRef | null;
    updatedBy: TaskUserRef | null;
    createdAt: string | null;
    updatedAt: string | null;
    deletedAt: string | null;
    subtasks?: Task[];
    taskDocuments?: any[];

    // Concurrency / idempotency. Optional so legacy code keeps compiling.
    version?: number;
    clientMutationId?: string;
    forceOverwrite?: boolean;
    overwriteReason?: string;
}

export interface TaskSearchCriteria {
    keyword?: string;
    status?: TaskStatus[];
    excludeStatuses?: TaskStatus[];
    priority?: TaskPriority[];
    assignedUserId?: number;
    createdBy?: number;
    parentType?: EntityParentType;
    parentId?: number;
    missionScopeId?: number;
    parentTaskId?: number | null;
    dueDateFrom?: string | null;
    dueDateTo?: string | null;
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDirection?: "ASC" | "DESC";
    isActive?: boolean;
}

export const INITIAL_TASK_SEARCH_CRITERIA: TaskSearchCriteria = {
    keyword: "",
    status: [],
    excludeStatuses: [TaskStatus.Completed, TaskStatus.Canceled],
    priority: [],
    assignedUserId: undefined,
    dueDateFrom: null,
    dueDateTo: null,
    page: 1,
    pageSize: 20,
    sortBy: "updatedAt",
    sortDirection: "DESC",
    isActive: true,
};

export const INITIAL_TASK: Task = {
    taskId: 0,
    referenceNumber: null,
    name: "",
    description: "",
    status: TaskStatus.NotStarted,
    priority: TaskPriority.Normal,
    parentType: null,
    parentId: null,
    parentReferenceNumber: null,
    parentTaskId: null,
    parentTaskReferenceNumber: null,
    sortOrder: null,
    startAt: null,
    endAt: null,
    actualStartAt: null,
    actualEndAt: null,
    assignedUser: null,
    createdBy: null,
    updatedBy: null,
    createdAt: null,
    updatedAt: null,
    deletedAt: null,
    taskDocuments: [],
};

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
    [TaskStatus.NotStarted]: "Not started",
    [TaskStatus.Started]: "Started",
    [TaskStatus.Completed]: "Completed",
    [TaskStatus.Canceled]: "Canceled",
};

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
    [TaskPriority.Low]: "Low",
    [TaskPriority.Normal]: "Normal",
    [TaskPriority.High]: "High",
    [TaskPriority.Urgent]: "Urgent",
};
